import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'

import ProductTypeChip from '../../../components/ProductTypeChip'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'
import { TooltipChip } from '../../../components/inspector/shared/TooltipChip'
import { renderProductNameWithMeta } from '../../exceptions/shared/productRenderers'
import {
  formatMainCategory,
  isMissingCategory,
  NO_CATEGORY_LABEL,
  NO_CATEGORY_VALUE,
} from '../../../utils/productCategory'
import type {
  ExceptionConfig,
  ExceptionFilterOption,
} from '../../exceptions/types'
import { formatMonthYear } from '../../../utils/inspectorFormatters'
import { fmtInt, fmtQtyShort } from './formatters'
import { AGING_LABELS } from './renderers'
import type { DormantItem } from './types'

const extractCategoryOptions = (
  rows: Record<string, any>[],
): ExceptionFilterOption[] => {
  // Preserve LS sync order via category_root_pk: lowest pk first.
  const byName = new Map<string, { pk: number; count: number }>()
  let noCat = 0
  for (const raw of rows as DormantItem[]) {
    if (isMissingCategory(raw.category_root_name)) {
      noCat += 1
      continue
    }
    const name = String(raw.category_root_name)
    const pk = raw.category_root_pk ?? Number.MAX_SAFE_INTEGER
    const existing = byName.get(name)
    if (existing === undefined) {
      byName.set(name, { pk, count: 1 })
    } else {
      byName.set(name, { pk: Math.min(existing.pk, pk), count: existing.count + 1 })
    }
  }
  const ordered: ExceptionFilterOption[] = Array.from(byName.entries())
    .sort((a, b) => a[1].pk - b[1].pk)
    .map(([name, { count }]) => ({ value: name, label: name, count }))
  if (noCat > 0) {
    ordered.push({
      value: NO_CATEGORY_VALUE,
      label: NO_CATEGORY_LABEL,
      count: noCat,
      variant: 'subtle',
    })
  }
  return ordered
}

export const dormantConfig: ExceptionConfig = {
  codes: [],
  complexity: 'comparison',
  rowKeyField: 'ls_id',
  defaultSortColumn: 'on_hand_units',
  defaultSortDirection: 'desc',
  clientDefaultPageSize: 100,
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search product name or SKU',
      placeholder: 'Search product name or SKU',
      predicate: (row, term) => {
        const t = term.toLowerCase()
        const r = row as DormantItem
        return (
          (r.name?.toLowerCase().includes(t) ?? false) ||
          (r.sku?.toLowerCase().includes(t) ?? false)
        )
      },
    },
    {
      key: 'category_root_name',
      type: 'multiselect',
      label: 'Main Category',
      extractOptions: extractCategoryOptions,
      getValue: (row) => {
        const raw = (row as DormantItem).category_root_name
        return isMissingCategory(raw) ? NO_CATEGORY_VALUE : String(raw)
      },
    },
    {
      key: 'product_type',
      type: 'multiselect',
      label: 'Type',
      options: [
        { value: 'simple', label: 'Standard' },
        { value: 'composite_kit', label: 'Composite (kit)' },
        { value: 'composite_dosage', label: 'Composite (dosage)' },
        { value: 'variant_parent', label: 'Variant (parent)' },
        { value: 'variant_child', label: 'Variant (child)' },
      ],
    },
    {
      // ON: products consumed by an active composite parent are treated
      // as having activity (via the parent's sales) and dropped from the
      // dormant list. OFF: every dormant row stays, even when the parent
      // composite is still selling.
      key: 'count_composite_as_activity',
      type: 'toggle',
      label: 'Count composite usage as activity',
      defaultEnabled: true,
      predicate: (row) => !(row as DormantItem).composite_active,
    },
  ],
  columns: [
    {
      key: 'name',
      label: 'Product',
      align: 'left',
      render: renderProductNameWithMeta,
    },
    {
      key: 'product_type',
      label: 'Type',
      align: 'center',
      render: (_v, row) => (
        <ProductTypeChip productType={(row as DormantItem).product_type} dense />
      ),
    },
    {
      key: 'category_root_name',
      label: 'Main Category',
      render: (v) => formatMainCategory(v as string | null),
    },
    {
      key: 'on_hand_units',
      label: 'Stock total',
      align: 'right',
      render: (v) => fmtInt(v as number),
    },
    {
      key: 'is_spl',
      label: 'PWX Std Prod List',
      render: (v) => ((v as boolean) ? 'Yes' : ''),
    },
    {
      key: 'aging',
      label: 'Aging',
      render: (_v, row) => AGING_LABELS[(row as DormantItem).aging],
    },
    {
      key: 'composite_parent_count',
      label: 'Used by composite',
      align: 'center',
      render: (_v, row) => {
        const r = row as DormantItem
        if (r.composite_parent_count === 0) return ''
        const skuLines = r.composite_parent_skus.join('\n') || '(no SKU on parent)'
        const lastSold = r.composite_parent_last_sold_local || 'never'
        const periodSales = fmtQtyShort(r.composite_parent_period_sales)
        return (
          <TooltipChip
            title={
              <Box sx={{ whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                {`${skuLines}\nLast parent sale: ${lastSold}\nParent sold in period: ${periodSales}`}
              </Box>
            }
          >
            <Chip
              {...statusPillChipProps(
                `${r.composite_parent_count}×`,
                r.composite_active ? 'warning' : 'neutral',
              )}
            />
          </TooltipChip>
        )
      },
    },
    {
      key: 'last_received_date_local',
      label: 'Received',
      align: 'center',
      render: (_v, row) => {
        const r = row as DormantItem
        if (!r.last_received_date_local) return ''
        return (
          <TooltipChip title={r.last_received_date_local}>
            <Chip
              {...statusPillChipProps(
                formatMonthYear(r.last_received_date_local),
                r.received_in_lookback ? 'warning' : 'neutral',
              )}
            />
          </TooltipChip>
        )
      },
    },
    {
      key: 'last_sale_date_local',
      label: 'Last sold',
      render: (_v, row) => {
        const r = row as DormantItem
        const effective =
          r.effective_source === 'direct' || r.effective_source === 'composite'
            ? r.effective_last_activity_date
            : r.last_sale_date_local
        const display = effective || '-'
        const directTooltip = `Direct: ${r.last_sale_date_local || 'never'}`
        return (
          <Tooltip arrow title={directTooltip}>
            <Stack direction="column" spacing={0} sx={{ lineHeight: 1.2 }}>
              <span>{display}</span>
              {r.effective_source === 'composite' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  (via composite)
                </Typography>
              )}
            </Stack>
          </Tooltip>
        )
      },
    },
  ],
}
